import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useOmniAuth } from '@omni/auth'

// Great leads, the one rule shared by the bell, the leads page and the alert
// email (Mortgage-Man-Command-Center scripts/leads/great-lead-alert.mjs):
//   a Typeform lead scoring 18 or more (the score that puts them in Trail's
//   Hot Leads), or a website lead the calculator marked qualified.
//
// "Seen" lives in this browser: opening the leads page clears the bell. A page
// shows, it never writes (Sam, 10 Oct 2026), so nothing here touches the
// database.

export const GREAT_SCORE = 18

export interface TypeformAnswer {
  title: string
  value: string
}

export interface TypeformLead {
  key: string
  token: string
  name: string
  email: string | null
  phone: string | null
  form: string
  score: number | null
  submittedAt: string
  trailStatus: string | null
  trailStage: string | null
  trailError: string | null
  source: string | null
  answers: TypeformAnswer[]
}

const TYPEFORM_COLS = 'token, form_title, submitted_at, score, status, trail_stage, push_error, lead'

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null)
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export function useTypeformLeads(opts: { refetchMs?: number } = {}) {
  const { getClient } = useOmniAuth()
  const db = useMemo(() => getClient('crm').schema('public'), [getClient])
  return useQuery<TypeformLead[]>({
    queryKey: ['tmm', 'typeform-leads'],
    refetchInterval: opts.refetchMs,
    queryFn: async () => {
      const res = await db.from('typeform_responses').select(TYPEFORM_COLS).order('submitted_at', { ascending: false }).limit(1000)
      if (res.error) throw new Error(`Typeform leads: ${res.error.message}`)
      return ((res.data ?? []) as Record<string, unknown>[]).map((r) => {
        const lead = (r.lead ?? {}) as Record<string, unknown>
        const answers = Array.isArray(lead.answers)
          ? (lead.answers as Record<string, unknown>[])
              .map((a) => ({ title: String(a.title ?? '').trim(), value: String(a.value ?? '').trim() }))
              .filter((a) => a.title && a.value)
          : []
        return {
          key: `t-${String(r.token)}`,
          token: String(r.token),
          name: str(lead.profileName) ?? str(lead.email) ?? '(no name)',
          email: str(lead.email),
          phone: str(lead.phone),
          form: String(r.form_title ?? 'Typeform'),
          score: num(r.score),
          submittedAt: String(r.submitted_at),
          trailStatus: str(r.status),
          trailStage: str(r.trail_stage),
          trailError: str(r.push_error),
          source: str(lead.source),
          answers,
        }
      })
    },
  })
}

/** One great lead, as the bell counts it. */
export interface GreatLead {
  key: string
  name: string
  at: string
}

/** Great leads from both sources, newest first. Website rows come from the leads page's own query. */
export function useGreatLeads(opts: { refetchMs?: number } = {}) {
  const { getClient } = useOmniAuth()
  const db = useMemo(() => getClient('crm').schema('public'), [getClient])
  const typeform = useTypeformLeads(opts)
  const website = useQuery<GreatLead[]>({
    queryKey: ['tmm', 'great-website-leads'],
    refetchInterval: opts.refetchMs,
    queryFn: async () => {
      const [funnel, apps] = await Promise.all([
        db.from('website_funnel_leads').select('id, name, created_at, trail_status').eq('qualified_tier', 'qualified').order('created_at', { ascending: false }).limit(200),
        db.from('website_applications').select('id, first_name, last_name, created_at, trail_status').eq('qualified_tier', 'qualified').order('created_at', { ascending: false }).limit(200),
      ])
      if (funnel.error) throw new Error(funnel.error.message)
      if (apps.error) throw new Error(apps.error.message)
      const notTest = (r: Record<string, unknown>) => r.trail_status !== 'skipped'
      return [
        ...((funnel.data ?? []) as Record<string, unknown>[]).filter(notTest).map((r) => ({ key: `f-${String(r.id)}`, name: str(r.name) ?? '(no name)', at: String(r.created_at) })),
        ...((apps.data ?? []) as Record<string, unknown>[]).filter(notTest).map((r) => ({
          key: `a-${String(r.id)}`,
          name: [str(r.first_name), str(r.last_name)].filter(Boolean).join(' ') || '(no name)',
          at: String(r.created_at),
        })),
      ]
    },
  })
  const data = useMemo<GreatLead[] | undefined>(() => {
    if (!typeform.data && !website.data) return undefined
    const t = (typeform.data ?? []).filter((l) => (l.score ?? 0) >= GREAT_SCORE).map((l) => ({ key: l.key, name: l.name, at: l.submittedAt }))
    return [...t, ...(website.data ?? [])].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
  }, [typeform.data, website.data])
  return { data, isError: typeform.isError && website.isError }
}

// --- seen, per browser -------------------------------------------------------

const SEEN_KEY = 'tmm-great-leads-seen'
const SEEN_EVENT = 'tmm-great-leads-seen'
// A browser that has never opened the page counts the last three days only,
// so the bell does not open on a number from the whole history.
const FIRST_LOOK_MS = 3 * 24 * 60 * 60 * 1000
// Fixed once per page load: useSyncExternalStore needs the same snapshot back.
const DEFAULT_SEEN = Date.now() - FIRST_LOOK_MS

function readSeen(): number {
  try {
    const v = Number(localStorage.getItem(SEEN_KEY))
    return Number.isFinite(v) && v > 0 ? v : DEFAULT_SEEN
  } catch {
    return DEFAULT_SEEN
  }
}

function subscribeSeen(cb: () => void) {
  window.addEventListener(SEEN_EVENT, cb)
  window.addEventListener('storage', cb)
  return () => {
    window.removeEventListener(SEEN_EVENT, cb)
    window.removeEventListener('storage', cb)
  }
}

/** Marks every great lead up to now as seen (the leads page calls this while open). */
export function useMarkGreatLeadsSeen(active: boolean) {
  useEffect(() => {
    if (!active) return
    try {
      localStorage.setItem(SEEN_KEY, String(Date.now()))
    } catch {
      /* private window: the bell just stays as it is */
    }
    window.dispatchEvent(new Event(SEEN_EVENT))
  }, [active])
}

/** The great leads that came in after this browser last opened the leads page. */
export function useUnseenGreatLeads(): GreatLead[] {
  const seen = useSyncExternalStore(subscribeSeen, readSeen, readSeen)
  const { data } = useGreatLeads({ refetchMs: 60_000 })
  return useMemo(() => (data ?? []).filter((l) => Date.parse(l.at) > seen), [data, seen])
}
